/**
 * RNR's Alert: a persistent, in-flow message about the state of the screen.
 *
 * Two changes from the registry component, both deliberate. The mark is a React node rather than a
 * Lucide icon, because this app draws its glyphs as text and pulling in lucide-react-native would
 * mean react-native-svg for a dependency nothing else here uses. And the row is a real flex row
 * rather than an absolutely-positioned icon, which is what keeps the mark aligned on native.
 *
 * Colour never carries the meaning alone — the title says it in words, because a colour-only alert
 * is invisible to a colour-blind reader and to a screenshot in a bug report.
 */
import { Text, TextClassContext } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { View } from 'react-native';

const alertVariants = cva('bg-card border-border w-full flex-row gap-3 rounded-lg border px-4 py-3', {
  variants: {
    variant: {
      default: '',
      destructive: 'border-destructive/40',
      success: 'border-success/40',
      warning: 'border-warning/40',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const alertMarkVariants = cva('text-heading', {
  variants: {
    variant: {
      default: 'text-muted-foreground',
      destructive: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

const alertTitleVariants = cva('font-semibold', {
  variants: {
    variant: {
      default: 'text-foreground',
      destructive: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type AlertProps = React.ComponentProps<typeof View> &
  React.RefAttributes<View> &
  VariantProps<typeof alertVariants> & {
    /** The mark in the left column. A glyph, not a decoration — it repeats what the variant says. */
    icon?: React.ReactNode;
  };

const AlertVariantContext = React.createContext<VariantProps<typeof alertVariants>['variant']>('default');

function Alert({ className, variant = 'default', icon, children, ...props }: AlertProps) {
  return (
    <AlertVariantContext.Provider value={variant}>
      <View role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
        {icon ? <Text className={alertMarkVariants({ variant })}>{icon}</Text> : null}
        <TextClassContext.Provider value={undefined}>
          <View className="min-w-0 flex-1 gap-1">{children}</View>
        </TextClassContext.Provider>
      </View>
    </AlertVariantContext.Provider>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<typeof Text>) {
  const variant = React.useContext(AlertVariantContext);
  return <Text className={cn(alertTitleVariants({ variant }), className)} {...props} />;
}

function AlertDescription({ className, ...props }: React.ComponentProps<typeof Text>) {
  return <Text variant="small" className={cn('text-muted-foreground', className)} {...props} />;
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
